import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../../../environments/environment";
import { ApiResponse } from "../announcement/announcement.models";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";
import { OnboardingEvent, OnboardingJourney } from "./onboarding-event.models";

/**
 * Onboarding journal (waba OnboardingEventClientApi / OnboardingEventAdminApi).
 *
 * Writes are fire-and-forget on purpose: reporting that onboarding went wrong must never be
 * able to make onboarding go wrong, so a failed track() is swallowed and never surfaces a toast.
 */
@Injectable({ providedIn: "root" })
export class OnboardingEventApiService {
  private readonly clientUrl = `${environment.wabaApiUrl}/v1/api/onboarding-event-client`;
  private readonly adminUrl = `${environment.wabaApiUrl}/v1/api/admin-ui/onboarding-event`;

  constructor(private http: HttpClient) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({
      "x-api-requestId": uuidv4(),
      "x-api-canal": "web",
    });
  }

  /** Journals one step for the signed-in company. Errors are intentionally ignored. */
  track(event: OnboardingEvent): Observable<void> {
    return this.http
      .post<ApiResponse<OnboardingEvent>>(`${this.clientUrl}/track`, event, {
        headers: this.headers(),
        context: SILENT_HTTP_CONTEXT,
      })
      .pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  /** Back office: the whole journey of one company, newest events first. */
  journey(companyReference: string, limit = 200): Observable<OnboardingJourney | undefined> {
    return this.http
      .get<ApiResponse<OnboardingJourney>>(`${this.adminUrl}/${companyReference}/journey`, {
        headers: this.headers(),
        params: new HttpParams().set("limit", limit),
      })
      .pipe(map((response) => response.body));
  }
}
